import type { IUserProfileView } from '@shared/services';
import { authApi } from '../../client';

/**
 * Session-authorized profile requests for the Profile page. Every call goes over `authApi` — the SDK's
 * axios instance carrying the adopted access token — because the user is already signed in and managing
 * their own account. These are the same auth-service endpoints admin-panel-ui uses (its `authAPI`
 * prefixes `api/v1`, so its bare `/users/...` paths are the `/api/v1/users/...` paths spelled out here,
 * matching auth-ui's `baseURL: ''` convention).
 */

export interface UpdateUserProfileParams {
  firstName: string;
  lastName: string;
  phone: string;
}

export interface ChangePasswordParams {
  /** The account's current password, proving identity for the session-authorized change. */
  oldPassword: string;
  newPassword: string;
}

/** Read the signed-in account's profile (names, email, phone, location, and API key). */
export async function getUserProfile(): Promise<IUserProfileView> {
  const { data } = await authApi.get<IUserProfileView>('/api/v1/users/profile');

  return data;
}

/**
 * Update the editable profile fields. Only `{firstName, lastName, phone}` are submitted — email and
 * location are read-only on this page, mirroring admin-panel-ui's contract.
 */
export async function updateUserProfile(params: UpdateUserProfileParams): Promise<void> {
  await authApi.put('/api/v1/users/profile', params);
}

/**
 * Change the account password from an active session. Distinct from the login-flow's forced change
 * (`POST /api/v1/auth/change-password` with a `changeToken`): this is session-authorized and proves
 * identity with the current password, exactly like admin-panel-ui. No new session is issued.
 */
export async function changePassword(params: ChangePasswordParams): Promise<void> {
  await authApi.put('/api/v1/users/password', params);
}
